import type { FieldPath } from 'react-hook-form'

import type { CustomerWizardInput } from './validation'

export type CustomerFieldPath = FieldPath<CustomerWizardInput>

export type WizardStepId = 'identity' | 'contact' | 'address' | 'nextOfKin' | 'consents' | 'review'

export interface WizardStep {
  id: WizardStepId
  label: string
  /** One line under the step heading, saying what this step is for. */
  description: string
  /**
   * What must be valid before the step is left. A parent path such as `residence` validates
   * everything beneath it, so a nested field cannot be skipped by omitting it from this list.
   */
  fields: CustomerFieldPath[]
}

/**
 * The order the desk works in: who the person is, how to reach them, where they live, who to call
 * if they cannot be reached, what they have agreed to, and finally what is about to be sent.
 */
export const WIZARD_STEPS: WizardStep[] = [
  {
    id: 'identity',
    label: 'Identity',
    description: 'The name, date of birth, and Ghana Card exactly as they appear on the card.',
    fields: [
      'branchId',
      'firstName',
      'middleName',
      'lastName',
      'gender',
      'dateOfBirth',
      'placeOfBirth',
      'nationality',
      'maritalStatus',
      'ghanaCardNumber',
      'ghanaCardIssueDate',
      'ghanaCardExpiryDate',
    ],
  },
  {
    id: 'contact',
    label: 'Contact',
    description: 'How the customer is reached, and the livelihood the account will be judged against.',
    fields: [
      'phoneNumber',
      'altPhoneNumber',
      'email',
      'occupation',
      'employerOrBusinessName',
      'monthlyIncomeEstimateMinor',
    ],
  },
  {
    id: 'address',
    label: 'Address',
    description: 'A residence address is required. A business address is captured where there is one.',
    fields: ['residence', 'hasBusinessAddress', 'business'],
  },
  {
    id: 'nextOfKin',
    label: 'Next of kin',
    description: 'Optional. An entry that is started must be complete enough to act on.',
    fields: ['nextOfKin'],
  },
  {
    id: 'consents',
    label: 'Consents',
    description: 'Read each statement to the customer. What they agree to is recorded with its version.',
    fields: [
      'consentAccountOperation',
      'consentCreditBureau',
      'consentMarketing',
      'consentBiometricCapture',
    ],
  },
  {
    id: 'review',
    label: 'Review',
    description: 'Exactly what will be sent. Nothing is submitted until this step is confirmed.',
    fields: [],
  },
]

export const stepIndexOf = (id: WizardStepId) => WIZARD_STEPS.findIndex((step) => step.id === id)

/**
 * The step a field belongs to, matched on the path prefix so that `residence.town` lands on the
 * address step and `nextOfKin.1.phoneNumber` on the next-of-kin step.
 */
export function stepForField(path: string): WizardStep | undefined {
  return WIZARD_STEPS.find((step) =>
    step.fields.some((field) => path === field || path.startsWith(`${field}.`)),
  )
}
